import { useState, useMemo } from 'react';
import {
  Button,
  Badge,
  Checkbox,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@aaas/shared';
import { Layers, X, Check } from 'lucide-react';
import { useSpaceListQuery } from '../hooks';
import { useAuth } from '@aaas/shared';

interface SpaceSelectorProps {
  selectedSpaceIds: string[];
  onChange: (spaceIds: string[]) => void;
  error?: string;
}

export function SpaceSelector({ selectedSpaceIds, onChange, error }: SpaceSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { idToken } = useAuth();

  // Fetch all spaces (no pagination limit for selector)
  const { data: spaceListResponse, isLoading } = useSpaceListQuery({
    idToken: idToken ?? undefined,
    skip: 0,
    limit: 100,
  });

  const spaces = spaceListResponse?.data?.items || [];

  // Filter spaces based on search and limit to 10
  const filteredSpaces = useMemo(() => {
    if (!searchQuery) return spaces.slice(0, 10);
    const query = searchQuery.toLowerCase();
    return spaces
      .filter(
        (space) =>
          space.name.toLowerCase().includes(query) ||
          space.description?.toLowerCase().includes(query)
      )
      .slice(0, 10);
  }, [spaces, searchQuery]);

  const toggleSpace = (spaceId: string) => {
    if (selectedSpaceIds.includes(spaceId)) {
      onChange(selectedSpaceIds.filter((id) => id !== spaceId));
    } else {
      onChange([...selectedSpaceIds, spaceId]);
    }
  };

  const selectedSpaces = spaces.filter((s) => selectedSpaceIds.includes(s.id));

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={`w-full justify-start min-h-10 h-auto py-2 ${error ? 'border-red-500' : ''}`}
            >
              {selectedSpaceIds.length === 0 ? (
                <>
                  <Layers className="mr-2 h-4 w-4 flex-shrink-0" />
                  {isLoading ? 'Loading spaces...' : 'Select spaces...'}
                </>
              ) : (
                <div className="flex items-center gap-2 flex-wrap w-full">
                  <Layers className="h-4 w-4 flex-shrink-0" />
                  {selectedSpaces.map((space) => (
                    <Badge key={space.id} variant="secondary" className="gap-1 pr-1">
                      <span>{space.name}</span>
                      <span
                        role="button"
                        tabIndex={0}
                        className="inline-flex items-center justify-center h-4 w-4 rounded-sm hover:bg-gray-200 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSpace(space.id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                            toggleSpace(space.id);
                          }
                        }}
                      >
                        <X className="h-3 w-3" />
                      </span>
                    </Badge>
                  ))}
                </div>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="p-0"
            align="start"
            style={{ width: 'var(--radix-popover-trigger-width)' }}
          >
            <Command>
              <CommandInput
                placeholder="Search spaces..."
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
              <CommandList>
                <CommandEmpty>{isLoading ? 'Loading...' : 'No spaces found.'}</CommandEmpty>
                <CommandGroup
                  heading={`Spaces${filteredSpaces.length === 10 ? ' (showing first 10)' : ''}`}
                >
                  {filteredSpaces.map((space) => {
                    const isSelected = selectedSpaceIds.includes(space.id);
                    return (
                      <CommandItem key={space.id} onSelect={() => toggleSpace(space.id)}>
                        <Checkbox
                          checked={isSelected}
                          className="mr-2"
                          onCheckedChange={() => toggleSpace(space.id)}
                        />
                        <div className="flex flex-col flex-1">
                          <span className="font-medium">{space.name}</span>
                          {space.description && (
                            <span className="text-xs text-muted-foreground line-clamp-1">
                              {space.description}
                            </span>
                          )}
                        </div>
                        {isSelected && <Check className="ml-auto h-4 w-4" />}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {selectedSpaceIds.length > 0 && (
          <Button variant="ghost" size="sm" onClick={() => onChange([])}>
            Clear All
          </Button>
        )}
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
      {selectedSpaceIds.length > 0 && (
        <p className="text-sm text-muted-foreground">
          {selectedSpaceIds.length} space{selectedSpaceIds.length === 1 ? '' : 's'} selected
        </p>
      )}
    </div>
  );
}
